export type DeliverabilityInput = {
  reservoirPressure: number;
  bottomHolePressure: number;
  gasRate: number;
};

export type BinomialResult = {
  A: number;
  B: number;
  r2: number;
  rmse: number;
};

export type ExponentialResult = {
  C: number;
  n: number;
  r2: number;
  rmse: number;
};

function linearRegression(
  x: number[],
  y: number[],
) {
  const n = x.length;

  if (n < 2) return null;

  const meanX =
    x.reduce((sum, v) => sum + v, 0) / n;

  const meanY =
    y.reduce((sum, v) => sum + v, 0) / n;

  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i += 1) {
    numerator +=
      (x[i] - meanX) *
      (y[i] - meanY);

    denominator +=
      (x[i] - meanX) ** 2;
  }

  if (Math.abs(denominator) < 1e-12) {
    return null;
  }

  const slope = numerator / denominator;
  const intercept = meanY - slope * meanX;

  return {
    slope,
    intercept,
  };
}

function calcR2(
  actual: number[],
  predicted: number[],
) {
  if (
    actual.length === 0 ||
    actual.length !== predicted.length
  ) {
    return 0;
  }

  const mean =
    actual.reduce((sum, v) => sum + v, 0) /
    actual.length;

  const ssTot = actual.reduce(
    (sum, v) => sum + (v - mean) ** 2,
    0,
  );

  const ssRes = actual.reduce(
    (sum, v, i) =>
      sum + (v - predicted[i]) ** 2,
    0,
  );

  if (ssTot < 1e-12) return 1;

  return 1 - ssRes / ssTot;
}

function calcRmse(
  actual: number[],
  predicted: number[],
) {
  if (
    actual.length === 0 ||
    actual.length !== predicted.length
  ) {
    return 0;
  }

  const mse =
    actual.reduce(
      (sum, v, i) =>
        sum + (v - predicted[i]) ** 2,
      0,
    ) / actual.length;

  return Math.sqrt(mse);
}

export function pressureSquaredDrop(
  reservoirPressure: number,
  bottomHolePressure: number,
) {
  return (
    reservoirPressure ** 2 -
    bottomHolePressure ** 2
  );
}

export function solveBinomialRate(
  deltaP2: number,
  A: number,
  B: number,
) {
  if (
    !Number.isFinite(deltaP2) ||
    deltaP2 <= 0
  ) {
    return 0;
  }

  if (Math.abs(B) < 1e-12) {
    if (A <= 0) return NaN;
    return deltaP2 / A;
  }

  const discriminant =
    A ** 2 + 4 * B * deltaP2;

  if (discriminant < 0) {
    return NaN;
  }

  const root1 =
    (-A + Math.sqrt(discriminant)) /
    (2 * B);

  const root2 =
    (-A - Math.sqrt(discriminant)) /
    (2 * B);

  const positiveRoots =
    [root1, root2].filter(
      (v) =>
        Number.isFinite(v) &&
        v >= 0,
    );

  if (positiveRoots.length === 0) {
    return NaN;
  }

  return Math.min(...positiveRoots);
}

export function fitBinomial(
  points: DeliverabilityInput[],
): BinomialResult | null {
  const valid = points.filter((p) => {
    const delta =
      pressureSquaredDrop(
        p.reservoirPressure,
        p.bottomHolePressure,
      );

    return (
      p.gasRate > 0 &&
      p.reservoirPressure >
        p.bottomHolePressure &&
      delta > 0
    );
  });

  if (valid.length < 3) return null;

  // Δp² / q = A + Bq
  const x = valid.map((p) => p.gasRate);

  const y = valid.map((p) => {
    const delta =
      pressureSquaredDrop(
        p.reservoirPressure,
        p.bottomHolePressure,
      );

    return delta / p.gasRate;
  });

  const regression =
    linearRegression(x, y);

  if (!regression) return null;

  const A = regression.intercept;
  const B = regression.slope;

  const actual =
    valid.map((p) => p.gasRate);

  const predicted =
    valid.map((p) => {
      const delta =
        pressureSquaredDrop(
          p.reservoirPressure,
          p.bottomHolePressure,
        );

      return solveBinomialRate(
        delta,
        A,
        B,
      );
    });

  if (
    predicted.some(
      (v) => !Number.isFinite(v),
    )
  ) {
    return {
      A,
      B,
      r2: NaN,
      rmse: NaN,
    };
  }

  return {
    A,
    B,
    r2: calcR2(actual, predicted),
    rmse: calcRmse(actual, predicted),
  };
}

export function fitExponential(
  points: DeliverabilityInput[],
): ExponentialResult | null {
  const valid = points.filter((p) => {
    const delta =
      pressureSquaredDrop(
        p.reservoirPressure,
        p.bottomHolePressure,
      );

    return (
      p.gasRate > 0 &&
      delta > 0
    );
  });

  if (valid.length < 3) return null;

  // ln(q) = ln(C) + n ln(Δp²)
  const x = valid.map((p) =>
    Math.log(
      pressureSquaredDrop(
        p.reservoirPressure,
        p.bottomHolePressure,
      ),
    ),
  );

  const y = valid.map((p) =>
    Math.log(p.gasRate),
  );

  const regression =
    linearRegression(x, y);

  if (!regression) return null;

  const n = regression.slope;
  const C = Math.exp(
    regression.intercept,
  );

  const actual =
    valid.map((p) => p.gasRate);

  const predicted =
    valid.map((p) => {
      const delta =
        pressureSquaredDrop(
          p.reservoirPressure,
          p.bottomHolePressure,
        );

      return C * delta ** n;
    });

  return {
    C,
    n,
    r2: calcR2(actual, predicted),
    rmse: calcRmse(actual, predicted),
  };
}

export function calcBinomialAof(
  reservoirPressure: number,
  A: number,
  B: number,
) {
  const atmosphericPressure = 0.101;

  const delta =
    pressureSquaredDrop(
      reservoirPressure,
      atmosphericPressure,
    );

  return solveBinomialRate(
    delta,
    A,
    B,
  );
}

export function calcExponentialAof(
  reservoirPressure: number,
  C: number,
  n: number,
) {
  const atmosphericPressure = 0.101;

  const delta =
    pressureSquaredDrop(
      reservoirPressure,
      atmosphericPressure,
    );

  if (
    delta <= 0 ||
    C <= 0 ||
    !Number.isFinite(n)
  ) {
    return NaN;
  }

  return C * delta ** n;
}